import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getUser,
  updateUserAttributes,
} from "@/lib/api/clients/UserClient";
import { qk } from "@/lib/query/queryKeys";
import type { User } from "@/types/User";

/**
 * Field set every generic user read asks for. Exported so the post-login
 * hydration (`hydrateAccountPresentation`) seeds `qk.user.me` with EXACTLY
 * this shape — a second, drifting field string would leave the cache holding
 * a narrower user than `useUser` consumers expect.
 *
 * Deliberately NOT `userId`: main-backend's `User.userId` is a raw `ObjectId`
 * with no `@JsonbTypeAdapter` (unlike ~180 other entities), so it does not
 * serialise to a hex string. The identity stamp therefore takes its id from
 * the auth response, which sends `userId.toHexString()`.
 */
export const DEFAULT_FIELDS = "monogramColor,firstName,lastName,email,languageCode,acceptCrossCompanyEventInvites,mfaEnabled,theme";

export function useUser(fields: string = DEFAULT_FIELDS) {
  return useQuery({
    queryKey: qk.user.me,
    queryFn: () => getUser(fields),
    staleTime: 60_000,
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (user: User) => updateUserAttributes(user),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.user.me }),
  });
}
